import Vue from 'vue';
import _ from 'lodash';
import {
  objectsSearch,
  objectDetails,
  objectCreate,
  objectUpdate,
  objectOrders,
  setObjectBlocking,
  importCsv,
  objectDelete, // eslint-disable-line import/named
} from '@/api/objects';
import {
  SET_OBJECTS,
  SET_OBJECT,
  SET_OBJECT_ORDERS,
  SET_OBJECT_BLOCKING,
} from './mutation-types';
import { prepareSubjectPayload } from './utils';

export default {
  state: {
    data: {
      payload: [],
      total: null,
    },
    objects: [],
    orders: {},
  },
  getters: {
    object: (state) => (id) => {
      if (typeof id !== 'undefined') {
        return state.objects.find((obj) => obj.id === parseInt(id, 10)) || null;
      }
      return state.objects;
    },
    orders: (state) => (id) => {
      if (typeof id !== 'undefined') {
        return _.get(state.orders, id) || null;
      }
      return state.orders;
    },
  },
  actions: {
    ACT_FETCH_OBJECTS: async ({ commit, dispatch }, {
      offset,
      limit,
      search,
      orderBy,
    }) => {
      dispatch('wait/start', 'fetch.objects', { root: true });
      return objectsSearch({
        offset,
        limit,
        search,
        orderBy,
      }).then((response) => {
        const data = _.get(response, 'data.result') || [];
        const total = _.get(response, 'data.total') || null;
        commit(SET_OBJECTS, {
          data,
          total,
        });
      }).finally(() => {
        dispatch('wait/end', 'fetch.objects', { root: true });
      });
    },
    ACT_FETCH_OBJECT: async ({
      commit,
      dispatch,
    }, id) => {
      dispatch('wait/start', `fetch.object.${id}`, { root: true });
      return objectDetails(id).then((response) => {
        const { data } = response;
        if (data) {
          commit(SET_OBJECT, data);
          return Promise.resolve(data);
        }
        return Promise.reject();
      }).finally(() => {
        dispatch('wait/end', `fetch.object.${id}`, { root: true });
      });
    },
    // eslint-disable-next-line arrow-body-style
    ACT_CREATE_OBJECT: async (context, payload) => {
      console.log('ACT_CREATE_OBJECT() -> payload', payload);
      // eslint-disable-next-line arrow-body-style
      return prepareSubjectPayload(context, payload).then((objectQuery) => {
        console.log('ACT_CREATE_OBJECT() -> objectQuery', objectQuery);
        return objectCreate(objectQuery).then((response) => {
          if (response.status === 200) {
            return Promise.resolve();
          }
          return Promise.reject();
        });
      });
    },
    ACT_UPDATE_OBJECT: async (context, {
      id,
      payload,
    }) => { // eslint-disable-line arrow-body-style
      console.log('ACT_UPDATE_OBJECT() -> payload', payload);
      // eslint-disable-next-line arrow-body-style
      return prepareSubjectPayload(context, payload).then((objectQuery) => {
        console.log('ACT_UPDATE_OBJECT() -> objectQuery', objectQuery);
        return objectUpdate(id, objectQuery).then((response) => {
          if (response.status === 200) {
            context.commit(SET_OBJECT, response.data);
            return Promise.resolve();
          }
          return Promise.reject();
        });
      });
    },
    ACT_OBJECT_ASSIGN_CUSTOMER: async ({ commit, getters }, {
      id,
      customer,
    }) => {
      const obj = getters.object(id);
      if (!obj) {
        return Promise.reject();
      }
      return objectUpdate(id, {
        id,
        customer,
      }).then((response) => {
        if (response.status === 200) {
          commit(SET_OBJECT, response.data);
          return Promise.resolve();
        }
        return Promise.reject();
      });
    },
    ACT_FETCH_OBJECT_ORDERS: async ({ commit, dispatch }, {
      id,
      limit,
      offset,
      orderBy,
    }) => {
      dispatch('wait/start', `fetch.object.orders.${id}`, { root: true });
      return objectOrders(id, {
        offset,
        limit,
        orderBy,
      }).then((response) => {
        const data = _.get(response, 'data.result') || [];
        const total = _.get(response, 'data.total') || null;
        if (data) {
          commit(SET_OBJECT_ORDERS, {
            id,
            data: data.map((order) => ({
              ...order,
              name: `${_.get(order, 'firstServiceCategory.abbreviation')}-${order.id}`,
            })),
            total,
          });
        }
      }).finally(() => {
        dispatch('wait/end', `fetch.object.orders.${id}`, { root: true });
      });
    },
    ACT_SET_OBJECT_BLOCKING: async ({ commit, dispatch }, { id, reason }) => {
      dispatch('wait/start', `block.object.${id}`, { root: true });
      return setObjectBlocking(id, reason).then((response) => {
        if (response.status === 200) {
          commit(SET_OBJECT_BLOCKING, {
            id,
            reason,
          });
        }
      }).finally(() => {
        dispatch('wait/end', `block.object.${id}`, { root: true });
      });
    },
    ACT_UPLOAD_CSV: async ({
      dispatch,
      rootGetters,
    }, fd) => {
      if (rootGetters['wait/is']('upload.object.csv')) {
        return Promise.reject();
      }
      dispatch('wait/start', 'upload.object.csv', { root: true });
      return importCsv(fd).then((response) => {
        if (response.status === 200) {
          return Promise.resolve();
        }
        return Promise.reject();
      }).finally(() => {
        dispatch('wait/end', 'upload.object.csv', { root: true });
      });
    },
    ACT_DELETE_OBJECT: async ({ dispatch }, id) => {
      dispatch('wait/start', `delete.object.${id}`, { root: true });
      return objectDelete(id).then((response) => {
        if (response.status === 200) {
          return Promise.resolve();
        }
        return Promise.reject();
      }).finally(() => {
        dispatch('wait/end', `delete.object.${id}`, { root: true });
      });
    },
  },
  mutations: {
    [SET_OBJECTS](state, { data, total }) {
      _.set(state, 'data.payload', data);
      _.set(state, 'data.total', total);
    },
    [SET_OBJECT](state, data) {
      const foundIndex = _.findIndex(
        state.objects,
        (obj) => parseInt(obj.id, 10) === parseInt(data.id, 10),
      );
      if (foundIndex > -1) {
        Vue.set(state.objects, foundIndex, data);
      } else {
        state.objects.push(data);
      }
    },
    [SET_OBJECT_ORDERS](state, { id, data, total }) {
      Vue.set(state.orders, id, {
        data,
        total,
      });
    },
    [SET_OBJECT_BLOCKING](state, { id, reason }) {
      state.objects = state.objects.map((obj) => {
        const data = obj;
        if (parseInt(obj.id, 10) === parseInt(id, 10)) {
          data.blockingReason = reason;
        }
        return data;
      });
    },
  },
  namespaced: true,
};
